import type { ArtistSelectOption } from '@/model/artist/ArtistSelectOption';
import type {
  BulkUploadArtistConnection,
  BulkUploadPreviewRow,
  BulkUploadUploadError,
  DeepPartial,
  ImportItemResult,
  ImportRow,
  InvalidItem,
} from '@/model/importer';
import { useInventoryStore } from '@/stores/InventoryStore/InventoryStore';
import type { ArtworkValidationValues } from '@/utils/artworkValidation';
import { createArtworkValidationSchema } from '@/utils/artworkValidation';
import {
  getArtistsService,
  type ArcualInventoryItem,
  type ArcualItemDimensions,
  type ArcualMediaItem,
  ArcualItemUnit,
  type Artist,
} from '@art-basel/artbasel-lib-platform';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { ValidationError } from 'yup';

const artworkValidationSchema = createArtworkValidationSchema();

const toArtworkValidationValues = (
  item: DeepPartial<ArcualInventoryItem>,
): ArtworkValidationValues => ({
  artistId: item.artistId,
  artistName: item.artistName,
  title: item.title,
  year: item.year,
  images: (item.images ?? []).flatMap((image) => {
    const url = image.url ?? image.id;
    return url ? [{ url }] : [];
  }),
  retailPrice: item.retailPrice?.amount,
  medium: item.medium,
  unit: item.dimensions?.unit,
  width: item.dimensions?.width,
  height: item.dimensions?.height,
  depth: item.dimensions?.depth,
  hours: item.dimensions?.hours,
  minutes: item.dimensions?.minutes,
  seconds: item.dimensions?.seconds,
  cubicMeters: item.dimensions?.cubicMeters,
  cubicFeet: item.dimensions?.cubicFeet,
  other: item.dimensions?.other,
});

const getValidationErrors = (item: DeepPartial<ArcualInventoryItem>): Array<string> => {
  try {
    artworkValidationSchema.validateSync(toArtworkValidationValues(item), { abortEarly: false });
    return [];
  } catch (validationError) {
    if (!(validationError instanceof ValidationError)) throw validationError;

    const errors = validationError.inner.length > 0 ? validationError.inner : [validationError];
    return errors.map(({ message, path }) => (path ? `${path}: ${message}` : message));
  }
};

const getErrorField = (message: string) => message.match(/^([A-Za-z][A-Za-z0-9]*)/)?.[1];

const normalizeArtistData = (value?: string) =>
  value
    ?.normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLocaleLowerCase() ?? '';

const getArtistConnectionId = (item: DeepPartial<ArcualInventoryItem>, row: number) => {
  const artistIdentity = [item.artistName, item.artistBirthYear, item.artistNationality]
    .map(normalizeArtistData);

  return artistIdentity.some(Boolean) ? artistIdentity.join('|') : `row-${row}`;
};

const isMatchingArtist = (item: DeepPartial<ArcualInventoryItem>, artist: Artist) => {
  const hasMatchingName = normalizeArtistData(`${artist.name} ${artist.surname}`) === normalizeArtistData(item.artistName);
  const hasMatchingBirthYear = !item.artistBirthYear || artist.birthYear === item.artistBirthYear;
  const artistNationalities = artist.nationalities.map(normalizeArtistData);
  const uploadedNationalities = item.artistNationality?.split(',').map(normalizeArtistData).filter(Boolean) ?? [];
  const hasMatchingNationality =
    uploadedNationalities.length === 0 || uploadedNationalities.some((nationality) => artistNationalities.includes(nationality));

  return hasMatchingName && hasMatchingBirthYear && hasMatchingNationality;
};

const toArtistSelectOption = (artist: Artist): ArtistSelectOption => ({
  key: artist.id,
  label: [
    `${artist.name} ${artist.surname}`.trim(),
    ...artist.nationalities,
    artist.birthYear,
  ].filter(Boolean).join(', '),
  value: artist.id,
  artistName: `${artist.name} ${artist.surname}`.trim(),
  artistBirthYear: artist.birthYear,
  artistNationality: artist.nationalities.filter(Boolean).join(', '),
});

const getLegacyRows = (result: ImportItemResult<ArcualInventoryItem>): Array<ImportRow<ArcualInventoryItem>> => {
  const invalidRows = (result.invalidItems ?? []).map<ImportRow<ArcualInventoryItem>>(({ row, invalidFields }) => ({
    row,
    item: {},
    invalidFields,
    duplicate: false,
  }));
  const occupiedRows = new Set(invalidRows.map(({ row }) => row));
  let row = 3;
  const validRows = result.items.map<ImportRow<ArcualInventoryItem>>((item) => {
    while (occupiedRows.has(row)) row += 1;

    const sourceRow = row;
    row += 1;
    return {
      row: sourceRow,
      item,
      invalidFields: [],
      duplicate: false,
    };
  });

  return [...validRows, ...invalidRows].sort((a, b) => a.row - b.row);
};

const toMediaItems = (item: DeepPartial<ArcualInventoryItem>): Array<ArcualMediaItem> =>
  (item.images ?? []).flatMap((image) => {
    const source = image.url ?? image.id;

    return source
      ? [{
        id: source,
        url: image.url ?? source,
        ...(image.description ? { description: image.description } : {}),
        ...(image.width !== undefined ? { width: image.width } : {}),
        ...(image.height !== undefined ? { height: image.height } : {}),
      }]
      : [];
  });

const toDimensions = (
  item: DeepPartial<ArcualInventoryItem>,
): ArcualItemDimensions | undefined => {
  const dimensions = item.dimensions;
  if (!dimensions?.unit) return undefined;

  const value = (dimensionValue?: string) => dimensionValue?.trim() || undefined;
  const withValue = (key: keyof Omit<ArcualItemDimensions, 'unit'>, dimensionValue?: string) => {
    const normalizedValue = value(dimensionValue);
    return normalizedValue ? { [key]: normalizedValue } : {};
  };

  switch (dimensions.unit) {
    case ArcualItemUnit.CM:
    case ArcualItemUnit.INCH:
      return {
        unit: dimensions.unit,
        ...withValue('width', dimensions.width),
        ...withValue('height', dimensions.height),
        ...withValue('depth', dimensions.depth),
      };
    case ArcualItemUnit.PX:
      return {
        unit: dimensions.unit,
        ...withValue('width', dimensions.width),
        ...withValue('height', dimensions.height),
      };
    case ArcualItemUnit.DURATION:
      return {
        unit: dimensions.unit,
        ...withValue('hours', dimensions.hours),
        ...withValue('minutes', dimensions.minutes),
        ...withValue('seconds', dimensions.seconds),
      };
    case ArcualItemUnit.VOLUME_CUBIC_METERS:
      return {
        unit: dimensions.unit,
        ...withValue('cubicMeters', dimensions.cubicMeters),
      };
    case ArcualItemUnit.VOLUME_CUBIC_FEET:
      return {
        unit: dimensions.unit,
        ...withValue('cubicFeet', dimensions.cubicFeet),
      };
    case ArcualItemUnit.OTHER:
      return {
        unit: dimensions.unit,
        ...withValue('other', dimensions.other),
      };
  }
};

const toUploadPayload = (
  row: BulkUploadPreviewRow,
  connection: BulkUploadArtistConnection | undefined,
): Omit<ArcualInventoryItem, 'id'> => {
  const { item } = row;
  const selectedArtist = connection?.selectedArtistOption;
  const images = toMediaItems(item);

  if (!selectedArtist || !item.title || !item.year || images.length === 0) {
    throw new Error(`Artwork on row ${row.row} is not ready to upload`);
  }

  const dimensions = toDimensions(item);
  const retailPrice = item.retailPrice?.amount !== undefined && item.retailPrice.currency
    ? {
      amount: item.retailPrice.amount,
      currency: item.retailPrice.currency,
    }
    : undefined;

  return {
    artistId: selectedArtist.value,
    artistName: selectedArtist.artistName,
    title: item.title,
    year: item.year,
    images,
    ...(selectedArtist.artistBirthYear ? { artistBirthYear: selectedArtist.artistBirthYear } : {}),
    ...(selectedArtist.artistNationality ? { artistNationality: selectedArtist.artistNationality } : {}),
    ...(item.artistBio ? { artistBio: item.artistBio } : {}),
    ...(item.category ? { category: item.category } : {}),
    ...(item.medium ? { medium: item.medium } : {}),
    ...(dimensions ? { dimensions } : {}),
    ...(item.description ? { description: item.description } : {}),
    ...(item.inventoryNumber ? { inventoryNumber: item.inventoryNumber } : {}),
    ...(retailPrice ? { retailPrice } : {}),
  };
};

const getUploadErrorMessage = (error: Error) => error.message;

export const BulkUploadStoreName = 'BulkUploadStore';
export const useBulkUploadStore = defineStore(
  BulkUploadStoreName,
  () => {
    const importResult = ref<ImportItemResult<ArcualInventoryItem> | null>(null);
    const artistConnections = ref<Array<BulkUploadArtistConnection>>([]);
    const isMatchingArtists = ref(false);
    const isUploading = ref(false);
    const isUploadModalOpen = ref(false);
    const uploadCount = ref(0);
    const uploadErrors = ref<Array<BulkUploadUploadError>>([]);
    const processedUploadRows = ref<Array<number>>([]);

    const allSourceRows = computed<Array<ImportRow<ArcualInventoryItem>>>(() => {
      if (!importResult.value) return [];

      return importResult.value.rows ?? getLegacyRows(importResult.value);
    });
    const sourceRows = computed(() => allSourceRows.value.filter(({ duplicate }) => !duplicate));

    const error = computed<Array<InvalidItem>>(() =>
      sourceRows.value.flatMap(({ row, item, invalidFields }) => {
        const validationErrors = getValidationErrors(item);
        const importerErrors = invalidFields.filter((message) => !/^artist(?:Id|Name)?\s*\(/i.test(message));
        const importerErrorFields = new Set(importerErrors.map(getErrorField));
        const additionalValidationErrors = validationErrors.filter((message) => {
          const field = getErrorField(message);
          return !field || !importerErrorFields.has(field);
        });
        const mergedErrors = [...new Set([...importerErrors, ...additionalValidationErrors])];

        return mergedErrors.length > 0 ? [{ row, invalidFields: mergedErrors }] : [];
      }),
    );

    const previewRows = computed<Array<BulkUploadPreviewRow>>(() => {
      const errorsByRow = new Map(error.value.map(({ row, invalidFields }) => [row, invalidFields]));

      return sourceRows.value.map(({ row, item }) => {
        const invalidFields = errorsByRow.get(row) ?? [];

        return {
          id: `bulk-upload-row-${row}`,
          row,
          item,
          invalidFields,
          hasErrors: invalidFields.length > 0,
        };
      });
    });

    const rowCount = computed(() =>
      allSourceRows.value.reduce((lastWorksheetRow, { row }) => Math.max(lastWorksheetRow, row), 0),
    );
    const errorCount = computed(() => error.value.length);
    const validItemCount = computed(() => previewRows.value.length - errorCount.value);
    const validRows = computed(() => previewRows.value.filter(({ hasErrors }) => !hasErrors));
    const allArtistsConnected = computed(() => validRows.value.length > 0 && validRows.value.every(({ row }) =>
      artistConnections.value.some(({ artworkRows, selectedArtistOption }) =>
        Boolean(selectedArtistOption) && artworkRows.includes(row),
      ),
    ));
    const isUploadComplete = computed(() => (
      validRows.value.length > 0 && validRows.value.every(({ row }) => processedUploadRows.value.includes(row))
    ));
    const uploadedItemCount = computed(() => Math.max(
      processedUploadRows.value.length - uploadErrors.value.length,
      0,
    ));

    const loadArtistConnections = async () => {
      isMatchingArtists.value = true;

      try {
        const connectionResults = await Promise.all(
          validRows.value.map(async ({ item, row }) => {
            if (!item.artistName) return { item, row, matchingArtist: undefined };

            try {
              const response = await getArtistsService().getArtistList({
                page: 1,
                pageSize: 20,
                searchOn: item.artistName,
                excludeIds: [],
              });

              return {
                item,
                row,
                matchingArtist: response.items.find((artist: Artist) => isMatchingArtist(item, artist)),
              };
            } catch {
              return { item, row, matchingArtist: undefined };
            }
          }),
        );
        const connections = new Map<string, BulkUploadArtistConnection>();

        connectionResults.forEach(({ item, row, matchingArtist }) => {
          const id = getArtistConnectionId(item, row);
          const existingConnection = connections.get(id);

          if (existingConnection) {
            existingConnection.artworkRows.push(row);
            if (!existingConnection.matchedArtist && matchingArtist) {
              existingConnection.matchedArtist = matchingArtist;
              existingConnection.selectedArtistOption = toArtistSelectOption(matchingArtist);
            }
            return;
          }

          connections.set(id, {
            id,
            artistName: item.artistName ?? '',
            artistBirthYear: item.artistBirthYear,
            artistNationality: item.artistNationality,
            artworkRows: [row],
            matchedArtist: matchingArtist,
            selectedArtistOption: matchingArtist ? toArtistSelectOption(matchingArtist) : undefined,
          });
        });

        artistConnections.value = [...connections.values()];
      } finally {
        isMatchingArtists.value = false;
      }
    };

    const updateArtistConnection = (connectionId: string, selectedArtistOption?: ArtistSelectOption) => {
      const connection = artistConnections.value.find(({ id }) => id === connectionId);
      if (connection) connection.selectedArtistOption = selectedArtistOption;
    };

    const resetUploadState = () => {
      isUploading.value = false;
      isUploadModalOpen.value = false;
      uploadCount.value = 0;
      uploadErrors.value = [];
      processedUploadRows.value = [];
    };

    const uploadItems = async () => {
      if (
        isUploading.value ||
        isUploadComplete.value ||
        !allArtistsConnected.value ||
        validRows.value.length === 0
      ) return;

      uploadCount.value = processedUploadRows.value.length;
      isUploading.value = true;
      isUploadModalOpen.value = true;

      try {
        const pendingRows = validRows.value.filter(({ row }) => !processedUploadRows.value.includes(row));

        for (const row of pendingRows) {
          const connection = artistConnections.value.find(({ artworkRows }) => artworkRows.includes(row.row));
          const identifier = row.item.inventoryNumber ?? `${row.item.title ?? ''} - ${connection?.artistName ?? ''}`;

          try {
            const payload = toUploadPayload(row, connection);
            await useInventoryStore().uploadItem(payload, true);
          } catch (error) {
            const uploadError = error instanceof Error ? error : new Error('Artwork upload failed');
            uploadErrors.value.push({
              identifier,
              message: getUploadErrorMessage(uploadError),
              row: row.row,
            });
          } finally {
            processedUploadRows.value.push(row.row);
            uploadCount.value += 1;
          }
        }
      } finally {
        isUploading.value = false;
      }
    };

    const closeUploadModal = () => {
      if (!isUploading.value) isUploadModalOpen.value = false;
    };

    const setImportResult = (newImportResult: ImportItemResult<ArcualInventoryItem>) => {
      artistConnections.value = [];
      resetUploadState();
      importResult.value = newImportResult;
    };

    const clearImportResult = () => {
      importResult.value = null;
      artistConnections.value = [];
      resetUploadState();
    };

    /**
     * Reset the store to its initial state
     */
    const reset = () => {
      clearImportResult();
    };

    return {
      setImportResult,
      allArtistsConnected,
      clearImportResult,
      closeUploadModal,
      artistConnections,
      error,
      errorCount,
      importResult,
      isMatchingArtists,
      isUploadComplete,
      isUploadModalOpen,
      isUploading,
      loadArtistConnections,
      previewRows,
      processedUploadRows,
      reset,
      rowCount,
      uploadCount,
      uploadErrors,
      uploadItems,
      uploadedItemCount,
      validItemCount,
      validRows,
      updateArtistConnection,
    };
  },
  { persist: { pick: ['importResult', 'artistConnections', 'processedUploadRows', 'uploadErrors'] } },
);
